import { useEffect } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';
import { z } from 'zod';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStartAPetitionPageData } from 'src/app/pages/startAPetitionV2/pageContext';
import type { PetitionDraft } from 'src/app/shared/api/petitionDraft';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useIsStarter } from 'src/app/shared/hooks/session';

export const requiredAiDraftSchema = z.object({
	type: z.string().optional().nullable(),
	petitionDraft: z.object({ ask: z.string(), description: z.string() }),
	recommendedDraft: z.object({ ask: z.string(), description: z.string() }),
});

export const requiredUserDraftSchema = z.object({
	type: z.string().optional().nullable(),
	petitionDraft: z.object({ ask: z.string(), description: z.string() }),
});

export type SapLandingStep = 'intro' | 'create' | 'preview';

export type SapEntryFromDraft = {
	landingStep: SapLandingStep;
	parseException?: unknown;
};

export function getSapEntryFromDraft(draft: PetitionDraft): SapEntryFromDraft {
	try {
		const schema = draft.type === 'user_draft' ? requiredUserDraftSchema : requiredAiDraftSchema;
		const draftResult = schema.safeParse(draft);

		if (draftResult.success) {
			const landingStep = draftResult.data.type === 'user_draft' ? 'create' : 'preview';
			return { landingStep };
		}

		return { landingStep: 'intro' };
	} catch (parseException) {
		return { landingStep: 'intro', parseException };
	}
}

function formatUnknownForErrorReport(value: unknown): string {
	if (value instanceof Error) return value.message;
	if (typeof value === 'string') return value;
	return JSON.stringify(value);
}

export function useRedirectHandler() {
	const { data } = useStartAPetitionPageData();
	const { errorReporter } = useUtilityContext();

	const { pathname, search } = useLocation();
	const pathWithoutStep = pathname.split('/').slice(0, -1).join('/');
	const sourceLocation = useQueryParamValue('source_location');
	const hasStartedFlowParam = search.includes('started_flow');
	const startedFlow = useQueryParamValue('started_flow') === 'true';
	const isStarter = useIsStarter();

	const track = useTracking();
	const navigate = useNavigate();

	useEffect(() => {
		if (startedFlow) return;

		const sapEntry = getSapEntryFromDraft(data.draft);

		if (sapEntry.parseException !== undefined) {
			const parseError = sapEntry.parseException;
			void errorReporter.report(
				{
					error: new Error('Failed to parse draft', {
						cause: parseError,
					}),
					params: {
						operation: 'useRedirectHandler',
						draftData: data.draft,
						parseError: formatUnknownForErrorReport(parseError),
					},
				},
				{
					severity: 'error',
				},
			);
		}

		const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true });
		const query = qs.stringify({ ...parsedQuery, started_flow: true });

		const hasDraft = sapEntry.landingStep !== 'intro';

		void track('sap_view', {
			source_location: sourceLocation,
			is_starter: isStarter,
			has_draft: hasDraft,
			...(!hasStartedFlowParam ? { redirected_to: sapEntry.landingStep } : {}),
		});

		if (hasStartedFlowParam) {
			navigate(`${pathname}?${query}`, { replace: true, preventScrollReset: true });
			return;
		}

		navigate(`${pathWithoutStep}/${sapEntry.landingStep}?${query}`, { replace: true, preventScrollReset: true });
	}, []);

	return { data: { startedFlow } };
}
